import React, { useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CameraTile from "./CameraTile";
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Crosshair } from "lucide-react";

const sevColor = (s) =>
  s === "critical" ? "sev-critical" :
  s === "high"     ? "sev-high" :
  s === "medium"   ? "sev-medium" :
                     "sev-low";

export const CameraFocusDialog = ({ camera, alerts = [], onClose }) => {
  const recent = useMemo(
    () => alerts.filter((a) => a.camera_id === camera?.id).slice(0, 8),
    [alerts, camera]
  );

  return (
    <Dialog open={!!camera} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-5xl border border-border bg-[hsl(var(--surface))] p-0"
        style={{ borderRadius: 2 }}
        data-testid="camera-focus-dialog"
      >
        <DialogHeader className="px-4 py-3 border-b border-border">
          <DialogTitle className="font-display font-black uppercase tracking-tight text-base flex items-center gap-3">
            <span className="font-mono text-[hsl(var(--primary))]">{camera?.id}</span>
            <span>{camera?.name}</span>
            {camera && (
              <span className="ml-auto micro-label flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${camera.status === "online" ? "bg-[hsl(var(--success))]" : "bg-[hsl(var(--warning))]"}`} />
                {camera.status} · {camera.ptz ? "PTZ" : "Fixed"}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {camera && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 p-2">
            {/* Main feed */}
            <div className="lg:col-span-2 aspect-video">
              <CameraTile camera={camera} hasAlert={recent.some((a) => a.status !== "resolved")} />
            </div>

            {/* Side rail */}
            <div className="flex flex-col gap-2">
              {/* PTZ controls */}
              <div className="panel-elevated p-3">
                <div className="h-widget mb-2 flex items-center gap-1">
                  <Crosshair className="w-3.5 h-3.5" /> PTZ Control
                </div>
                <div className="grid grid-cols-3 gap-1">
                  <span />
                  <button className="btn-tac" data-testid="ptz-up"><ChevronUp className="w-4 h-4" /></button>
                  <span />
                  <button className="btn-tac" data-testid="ptz-left"><ChevronLeft className="w-4 h-4" /></button>
                  <button className="btn-tac-primary" data-testid="ptz-home">Home</button>
                  <button className="btn-tac" data-testid="ptz-right"><ChevronRight className="w-4 h-4" /></button>
                  <span />
                  <button className="btn-tac" data-testid="ptz-down"><ChevronDown className="w-4 h-4" /></button>
                  <span />
                </div>
                <div className="mt-2 flex gap-1">
                  <button className="btn-tac flex-1" data-testid="ptz-zoom-in"><ZoomIn className="w-3.5 h-3.5" /> Zoom +</button>
                  <button className="btn-tac flex-1" data-testid="ptz-zoom-out"><ZoomOut className="w-3.5 h-3.5" /> Zoom −</button>
                </div>
                {!camera.ptz && (
                  <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Fixed camera · controls disabled
                  </div>
                )}
              </div>

              {/* Detection metadata */}
              <div className="panel-elevated p-3">
                <div className="h-widget mb-2">Detection Metadata</div>
                <ul className="space-y-1 font-mono text-[11px]">
                  <li className="flex justify-between"><span className="text-muted-foreground">Model</span><span>yolo-v11-ops</span></li>
                  <li className="flex justify-between"><span className="text-muted-foreground">FPS</span><span>24</span></li>
                  <li className="flex justify-between"><span className="text-muted-foreground">Bitrate</span><span>4.2 Mbps</span></li>
                  <li className="flex justify-between"><span className="text-muted-foreground">Resolution</span><span>1920×1080</span></li>
                  <li className="flex justify-between"><span className="text-muted-foreground">Tracker</span><span>byte-track</span></li>
                  <li className="flex justify-between"><span className="text-muted-foreground">Active tracks</span><span>{recent.length + 4}</span></li>
                </ul>
              </div>

              {/* Recent events on this camera */}
              <div className="panel-elevated p-3 flex-1 min-h-0">
                <div className="h-widget mb-2">Recent events</div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {recent.length === 0 && (
                    <div className="font-mono text-[11px] text-muted-foreground">No recent events on this feed.</div>
                  )}
                  {recent.map((a) => (
                    <div key={a.id} className="border-l-2 pl-2 py-0.5 text-[11px]"
                         style={{ borderColor: a.severity === "critical" ? "hsl(var(--destructive))" : a.severity === "high" ? "hsl(var(--destructive))" : a.severity === "medium" ? "hsl(var(--warning))" : "hsl(var(--primary))" }}>
                      <div className={`uppercase tracking-widest text-[10px] font-bold ${sevColor(a.severity)}`}>{a.severity}</div>
                      <div className="font-display font-bold leading-tight">{a.title}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">
                        {new Date(a.ts).toLocaleTimeString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CameraFocusDialog;
